import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthContextType } from '../types/auth';
import posthog from '../util/posthog';
import { useConfig } from './useConfig';
import { useLocalStorage } from './useLocalStorage';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useLocalStorage('user', null);
  const [, setContractsFilters] = useLocalStorage('contractsFilters', null);
  const [loginFailed, setLoginFailed] = useState(false);
  const navigate = useNavigate();

  const value = useMemo(() => {
    const login = async (username: string, password: string, fromPath: string, apiHost: string): Promise<void> => {
      // build headers
      const loginHeaders = new Headers();
      loginHeaders.append('Accept', 'application/json');
      loginHeaders.append('Accept-Language', 'en-US,en;q=0.9');
      loginHeaders.append('Cache-Control', 'no-cache');
      loginHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

      // build request body
      const urlencoded = new URLSearchParams();
      urlencoded.append('utf8', '✓');
      urlencoded.append('user[email]', username);
      urlencoded.append('user[password]', password);

      // build requset options
      const requestOptions: RequestInit = {
        method: 'POST',
        headers: loginHeaders,
        body: urlencoded,
        redirect: 'follow',
        credentials: 'include',
      };

      // execute request
      fetch(`${apiHost}/d_/users/sign_in`, requestOptions)
        .then(async (response) => {
          const respBody = await response.json();
          const respHeaders = response.headers;
          // read status code before returning response
          if (response.status === 200) {
            respBody.jwt_token = respHeaders.get('X-Token');
            setUser(respBody);
            posthog.identify(respBody.id?.toString() ?? respBody.email, {
              email: respBody.email,
              name: respBody.name,
              title: respBody.title,
              client_id: respBody.client_id,
              has_dashboard_access: respBody.has_dashboard_access,
            });
            posthog.group('client', respBody.client_id.toString());
            posthog.capture('user_logged_in', { login_type: 'password' });
            navigate(fromPath, { replace: true });
          } else {
            setLoginFailed(respBody.error);
          }
          return respBody;
        })
        .catch(() => {});
    };

    const ssoLogin = (apiHost: string): void => {
      posthog.capture('sso_login_initiated');
      const currentHost = window.location.host;
      const suffix = import.meta.env.VITE_IS_FEDERATED === 'true' ? '/onedsp' : '';
      window.location.href = `${apiHost}/d_/users/auth/ninthdecimal?origin=//${currentHost}${suffix}/sso/auth`;
    };

    // log out and destroy session
    const logout = (apiHost: string): void => {
      const loginHeaders = new Headers();
      loginHeaders.append('Accept', 'application/json');
      loginHeaders.append('Accept-Language', 'en-US,en;q=0.9');
      loginHeaders.append('Cache-Control', 'no-cache');
      loginHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

      const requestOptions: RequestInit = {
        method: 'DELETE',
        headers: loginHeaders,
        redirect: 'follow',
        credentials: 'include',
      };

      fetch(`${apiHost}/d_/users/sign_out`, requestOptions)
        .then((response) => response.json())
        .then(() => {
          posthog.capture('user_logged_out');
          posthog.reset();
          setUser(null);
          setContractsFilters([]);
          navigate('/login', { replace: true });
        });
    };

    // Allows for checking validity of session against API
    const checkSession = async (fromPath: string, apiHost: string): Promise<void> => {
      const loginHeaders = new Headers();
      loginHeaders.append('Accept', 'application/json');
      loginHeaders.append('Accept-Language', 'en-US,en;q=0.9');
      loginHeaders.append('Cache-Control', 'no-cache');
      loginHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

      const requestOptions: RequestInit = {
        method: 'GET',
        headers: loginHeaders,
        redirect: 'follow',
        credentials: 'include',
      };

      fetch(`${apiHost}/d_/users/check_active`, requestOptions).then(async (response) => {
        const respBody = await response.json();
        const respHeaders = response.headers;
        if (response.status === 200) {
          respBody.sso_token = respHeaders.get('X-Token');
          setUser(respBody);
          posthog.identify(respBody.id?.toString() ?? respBody.email, {
            email: respBody.email,
            name: respBody.name,
            title: respBody.title,
            client_id: respBody.client_id,
            has_dashboard_access: respBody.has_dashboard_access,
          });
          posthog.group('client', respBody.client_id.toString());
          posthog.capture('user_session_restored');
          navigate(fromPath, { replace: true });
          return respBody;
        }
        navigate('/login', { replace: true });
        return respBody;
      });
    };
    return {
      checkSession,
      user,
      loginFailed,
      login,
      logout,
      ssoLogin,
    };
  }, [user, loginFailed, navigate, setUser, setContractsFilters]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextType => useContext(AuthContext);

export function AuthStatus() {
  const auth = useAuth();
  const { apiHost } = useConfig();

  if (!auth.user) {
    return <p>You are not logged in.</p>;
  }

  return (
    <p>
      Welcome {auth.user.email}!{' '}
      <button
        type="button"
        onClick={() => {
          auth.logout(apiHost);
        }}
      >
        Sign out
      </button>
    </p>
  );
}
